import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api.js";
import { Badge, Button, EmptyState, Eyebrow, Field, LoadingRows, Notice, PageHeader, Panel, SearchCombobox, SelectField, cn } from "../components/ui.jsx";
import ListGrid from "./lists/ListGrid.jsx";
import { ColumnBuilder, CreateListPanel, EntityAdder, StageSettings } from "./lists/ListPanels.jsx";

const PAGE_SIZE = 50;

function updateFieldValue(list, entityId, fieldId, value) {
  return {
    ...list,
    rows: list.rows.map((row) => {
      if (row.entity.id === entityId) return { ...row, values: { ...row.values, [fieldId]: value } };
      if (!row.nested_people) return row;
      return {
        ...row,
        nested_people: row.nested_people.map((person) => (
          person.id === entityId ? { ...person, values: { ...person.values, [fieldId]: value } } : person
        )),
      };
    }),
  };
}

function LeadFromRowPanel({ list, row, onCreated, onCancel }) {
  const [form, setForm] = useState({ name: `${row.entity.name} opportunity`, department: "", company_id: "", priority: "normal" });
  const [companies, setCompanies] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event) {
    event.preventDefault(); setBusy(true); setError("");
    try { const result = await api.createLeadFromListRow(list.id, row.id, { name: form.name, department: form.department || null, company_id: form.company_id || null, priority: form.priority }); onCreated(result.lead); }
    catch (requestError) { if (requestError.code === "company_selection_required") setCompanies(requestError.details?.companies || []); setError(requestError.message); }
    finally { setBusy(false); }
  }
  return <Panel className="ring-1 ring-primary/25"><form className="grid gap-4 sm:grid-cols-2" onSubmit={submit}><div className="col-span-full"><Eyebrow>Promote from list</Eyebrow><h2 className="mt-1 text-xl font-bold tracking-[-0.02em]">Create a Lead from {row.entity.name}</h2></div>{error && <div className="col-span-full"><Notice>{error}</Notice></div>}<Field label="Lead name" value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} required autoFocus /><Field label="Department" hint="optional" value={form.department} onChange={(event) => setForm((current) => ({ ...current, department: event.target.value }))} />{companies.length > 0 && <SearchCombobox label="Company" value={form.company_id} onChange={(company_id) => setForm((current) => ({ ...current, company_id }))} options={companies.map((company) => ({ value: company.id, label: company.name, description: company.status || "Linked company" }))} placeholder="Search linked companies…" emptyMessage="No matching linked companies" required />}<SelectField label="Priority" value={form.priority} onChange={(event) => setForm((current) => ({ ...current, priority: event.target.value }))}><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option></SelectField><div className="col-span-full flex justify-end gap-2"><Button onClick={onCancel}>Cancel</Button><Button variant="primary" type="submit" disabled={busy}>{busy ? "Creating…" : "Create Lead"}</Button></div></form></Panel>;
}

export default function ListsPage({ onOpenCompany, onOpenPerson, onOpenLead }) {
  const [lists, setLists] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [listState, setListState] = useState({ loading: false, list: null });
  const [mode, setMode] = useState(null);
  const [error, setError] = useState("");
  const [booting, setBooting] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [leadRow, setLeadRow] = useState(null);
  const listRequest = useRef(0);

  const loadLists = useCallback(async (preferredId) => {
    try {
      const data = await api.lists();
      setLists(data.lists);
      setSelectedId((current) => preferredId || (data.lists.some((list) => list.id === current) ? current : data.lists[0]?.id || null));
      setError("");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBooting(false);
    }
  }, []);

  const loadList = useCallback(async () => {
    if (!selectedId) { listRequest.current += 1; setListState({ loading: false, list: null }); return; }
    const requestNumber = ++listRequest.current;
    setListState((current) => ({ ...current, loading: true }));
    try {
      const data = await api.list(selectedId, { limit: PAGE_SIZE });
      if (requestNumber !== listRequest.current) return;
      setListState({ loading: false, list: data.list });
      setError("");
    } catch (requestError) {
      if (requestNumber !== listRequest.current) return;
      setListState({ loading: false, list: null });
      setError(requestError.message);
    }
  }, [selectedId]);

  useEffect(() => { loadLists(); }, [loadLists]);
  useEffect(() => { loadList(); }, [loadList]);

  async function refreshStructure() {
    setMode(null);
    await Promise.all([loadList(), loadLists(selectedId)]);
  }

  async function loadMore() {
    const current = listState.list;
    if (!current?.page?.next_cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await api.listRows(current.id, { limit: PAGE_SIZE, cursor: current.page.next_cursor });
      setListState(({ list }) => ({
        loading: false,
        list: { ...list, rows: [...list.rows, ...data.rows], page: data.page },
      }));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoadingMore(false);
    }
  }

  async function loadPeople(companyId) {
    const listId = selectedId;
    setListState(({ list }) => ({
      loading: false,
      list: { ...list, rows: list.rows.map((row) => row.entity.id === companyId ? { ...row, nested_loading: true } : row) },
    }));
    try {
      const data = await api.listPeople(listId, companyId);
      setListState(({ list }) => ({
        loading: false,
        list: list?.id === listId ? { ...list, rows: list.rows.map((row) => row.entity.id === companyId ? { ...row, nested_loading: false, nested_people: data.people } : row) } : list,
      }));
    } catch (requestError) {
      setListState(({ list }) => ({
        loading: false,
        list: list?.id === listId ? { ...list, rows: list.rows.map((row) => row.entity.id === companyId ? { ...row, nested_loading: false } : row) } : list,
      }));
      setError(requestError.message);
    }
  }

  function changeField(entityId, fieldId, value) {
    setListState(({ list }) => ({ loading: false, list: updateFieldValue(list, entityId, fieldId, value) }));
  }

  function changeStage(rowId, stage) {
    setListState(({ list }) => ({
      loading: false,
      list: { ...list, rows: list.rows.map((row) => row.id === rowId ? { ...row, stage } : row) },
    }));
  }

  async function removeRow(row) {
    const previous = listState.list;
    setListState(({ list }) => ({
      loading: false,
      list: { ...list, row_count: Math.max(0, list.row_count - 1), rows: list.rows.filter((item) => item.id !== row.id) },
    }));
    try {
      await api.updateListRow(row.id, { lifecycle: "removed" });
      setLists((current) => current.map((item) => item.id === selectedId ? { ...item, row_count: Math.max(0, item.row_count - 1) } : item));
    } catch (requestError) {
      setListState({ loading: false, list: previous });
      setError(requestError.message);
    }
  }

  function created(list) {
    setMode(null);
    loadLists(list.id);
  }

  const list = listState.list;
  return (
    <div className="grid gap-8">
      <PageHeader eyebrow="Tables" title="Lists" description="Build multiple Clay-style views over the same canonical company and people memory." action={<Button variant="primary" onClick={() => setMode(mode === "create" ? null : "create")}>New list</Button>} />
      {error && <Notice>{error} <Button variant="ghost" className="ml-2 min-h-8 px-2" onClick={() => { loadLists(); loadList(); }}>Retry</Button></Notice>}
      {mode === "create" && <CreateListPanel onCreated={created} onCancel={() => setMode(null)} />}
      <div className="grid gap-6 xl:grid-cols-[220px_minmax(0,1fr)]">
        <aside>
          <div className="flex min-h-[58px] items-center justify-between"><Eyebrow>Your lists</Eyebrow><Badge>{lists.length}</Badge></div>
          {booting ? <LoadingRows count={2} /> : lists.length ? <div className="grid gap-1">{lists.map((item) => <button type="button" className={cn("grid min-h-[64px] grid-cols-[1fr_auto] items-center gap-3 rounded-lg px-3 text-left transition-colors hover:bg-muted", selectedId === item.id && "bg-accent hover:bg-accent")} key={item.id} onClick={() => { setSelectedId(item.id); setMode(null); }}><span className="grid min-w-0 gap-1"><strong className="truncate text-[13px]">{item.name}</strong><small className="text-[11px] uppercase tracking-[.08em] text-muted-foreground">{item.entity_type}</small></span><Badge>{item.row_count}</Badge></button>)}</div> : <p className="rounded-xl border border-dashed border-border p-4 text-xs leading-5 text-muted-foreground">Create Retail, IT companies, target accounts, hiring leads, or any other focused table.</p>}
        </aside>
        <section className="min-w-0">
          {!selectedId && !booting ? <EmptyState title="Create your first list" description="Each list can have its own columns and stages while sharing the same company and people records." /> : listState.loading && !list ? <LoadingRows count={5} /> : list && <div className="grid gap-5">
            <div className="grid gap-4 sm:flex sm:items-start sm:justify-between">
              <div className="grid gap-1"><div className="flex flex-wrap items-center gap-2"><h2 className="text-xl font-bold tracking-[-0.02em]">{list.name}</h2><Badge>{list.entity_type}</Badge></div><p className="max-w-[70ch] text-[13px] leading-6 text-muted-foreground">{list.description || `${list.row_count} canonical ${list.entity_type} records in this view.`}</p></div>
              <div className="flex flex-wrap gap-2"><Button onClick={() => setMode(mode === "stages" ? null : "stages")}>Stages</Button><Button onClick={() => setMode(mode === "column" ? null : "column")}>Add column</Button><Button variant="primary" onClick={() => setMode(mode === "row" ? null : "row")}>Add {list.entity_type}</Button></div>
            </div>
            {mode === "row" && <EntityAdder list={list} onAdded={refreshStructure} onCancel={() => setMode(null)} />}
            {mode === "column" && <ColumnBuilder list={list} onAdded={refreshStructure} onCancel={() => setMode(null)} />}
            {mode === "stages" && <StageSettings list={list} onSaved={refreshStructure} onCancel={() => setMode(null)} />}
            {leadRow && <LeadFromRowPanel list={list} row={leadRow} onCancel={() => setLeadRow(null)} onCreated={(lead) => { setLeadRow(null); onOpenLead(lead.id); }} />}
            <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[.06em] text-muted-foreground"><span>{list.row_count} rows</span><span>·</span><span>{list.columns.filter((column) => column.entity_level === "primary").length} primary fields</span>{list.entity_type === "company" && <><span>·</span><span>{list.columns.filter((column) => column.entity_level === "nested_person").length} people fields</span></>}</div>
            <ListGrid list={list} onOpenCompany={onOpenCompany} onOpenPerson={onOpenPerson} onCreateLead={setLeadRow} onFieldChange={changeField} onStageChange={changeStage} onRemove={removeRow} onLoadPeople={loadPeople} onLoadMore={loadMore} loadingMore={loadingMore} onError={setError} />
          </div>}
        </section>
      </div>
    </div>
  );
}
